
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { importQuizAikenQuestions, deleteExistingQuizAikenQuestions } from '@/services/quiz-aiken-service';
import { Upload, Info, Eye, EyeOff } from "lucide-react";
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import AikenPreview from './AikenPreview';

interface QuizAikenImportProps {
  subject: string;
  onImport: () => void;
}

const QuizAikenImport: React.FC<QuizAikenImportProps> = ({ subject, onImport }) => {
  const [isImporting, setIsImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [fileContent, setFileContent] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      
      // Read file content for preview
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result as string;
        setFileContent(content || '');
      };
      reader.readAsText(selectedFile);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }

    setIsImporting(true);
    setProgress(10);

    try {
      // First clear existing Aiken-imported questions for this subject
      setProgress(30);
      const deleteSuccess = await deleteExistingQuizAikenQuestions(subject);
      
      if (!deleteSuccess) {
        toast.error("Failed to clear existing questions");
        setIsImporting(false);
        return;
      }

      setProgress(50);
      
      // Now import the new questions
      const result = await importQuizAikenQuestions(file, subject);
      
      setProgress(100);
      
      if (result.importedCount > 0) {
        const difficultyMessage = result.importedCount > 1 
          ? "Questions have been automatically categorized by difficulty for adaptive learning." 
          : "Question has been automatically categorized by difficulty for adaptive learning.";
          
        toast.success(`Successfully imported ${result.importedCount} questions for ${subject}`, {
          description: difficultyMessage
        });
        onImport();
      } else {
        toast.error("No questions were imported. Please check the file format.");
      }
      
      if (result.errors.length > 0) {
        console.error("Import errors:", result.errors);
        toast.warning(`There were ${result.errors.length} errors during import`);
      }
    } catch (error) {
      console.error("Error during import:", error);
      toast.error("Failed to import questions");
    } finally {
      setIsImporting(false);
      setProgress(0);
      setFile(null);
      setFileContent('');
      setShowPreview(false);
    }
  };

  const togglePreview = () => {
    setShowPreview(prev => !prev);
  };

  return (
    <div className="mt-2 flex flex-col gap-2">
      <div className="flex items-center mb-1">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center text-xs text-gray-500 gap-1 hover:text-gray-700 cursor-help transition-colors">
                <Info className="h-3 w-3" />
                <span>About Difficulty Categorization</span>
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p>Questions are automatically categorized as easy, medium, or hard based on:</p>
              <ul className="list-disc ml-4 mt-1 text-xs">
                <li>Question length and complexity</li>
                <li>Number of sentences and newlines</li>
                <li>Answer option length and word count</li>
                <li>Presence of mathematical or technical content</li>
              </ul>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      
      <div className="flex gap-2">
        <input
          type="file"
          accept=".txt"
          onChange={handleFileChange}
          className="block w-full text-sm text-gray-500
            file:mr-4 file:py-1 file:px-2
            file:rounded-md file:border file:border-gray-200
            file:text-xs file:font-medium
            file:bg-gray-50 file:text-gray-700
            hover:file:bg-gray-100 file:cursor-pointer
            disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isImporting}
        />
        <Button
          size="sm"
          className="flex items-center gap-1 text-xs"
          onClick={handleImport}
          disabled={!file || isImporting}
        >
          <Upload className="h-3.5 w-3.5" />
          <span>{isImporting ? "Importing..." : "Import Aiken"}</span>
        </Button>
        
        {file && (
          <Button
            size="sm"
            variant="outline"
            className="flex items-center gap-1 text-xs"
            onClick={togglePreview}
          >
            {showPreview ? (
              <>
                <EyeOff className="h-3.5 w-3.5" />
                <span>Hide Preview</span>
              </>
            ) : (
              <>
                <Eye className="h-3.5 w-3.5" />
                <span>Preview</span>
              </>
            )}
          </Button>
        )}
      </div>
      
      {isImporting && (
        <div className="w-full">
          <Progress value={progress} className="h-1" />
          <div className="text-xs text-gray-500 mt-1 text-right">{progress}% complete</div>
        </div>
      )}
      
      {showPreview && fileContent && (
        <AikenPreview content={fileContent} />
      )}
    </div>
  );
};

export default QuizAikenImport;
